import { Fragment, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, RotateCcw, Share2, ChevronRight, ChevronLeft, Loader2, AlertCircle, Check } from 'lucide-react';
import { simulatorApi } from '../../services/api';
import type { SimulatorBracket, SimTeam } from '../../types';

/** Atalho i18n do simulador: t('simulator.<sufixo>', defaultPtBr, opts?). */
type Sk = (suffix: string, def: string, opts?: Record<string, unknown>) => string;

/** Strings já resolvidas passadas ao canvas (i18next não é acoplado ao Canvas). */
interface ShareImageLabels {
  headerPrefix: string;
  groupStage: string;
  bestThirds: string;
  knockout: string;
  champion: string;
  runnerUp: string;
  footer: string;
  toDefine: string;
  groupName: (g: { name: string; nameKey?: string }) => string;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface BracketMatch {
  id: string;
  team1: SimTeam | null;
  team2: SimTeam | null;
  winner: SimTeam | null;
}

type Step = 'loading' | 'error' | 'groups' | 'best_thirds' | 'liberta_thirds' | 'bracket' | 'champion';
type ShareState = 'idle' | 'generating' | 'done';

// ─── Helper: nome localizado do time ─────────────────────────────────────────
const getTeamName = (team: SimTeam): string => team.ptName ?? team.name;

// ─── LocalStorage helpers ─────────────────────────────────────────────────────

function loadSim(extId: number): Record<string, unknown> | null {
  try { return JSON.parse(localStorage.getItem(`sim_${extId}`) || 'null'); }
  catch { return null; }
}

function saveSim(extId: number, data: object) {
  try { localStorage.setItem(`sim_${extId}`, JSON.stringify(data)); }
  catch { /* quota exceeded */ }
}

// ─── Bracket generation ───────────────────────────────────────────────────────

// Extrai a letra do grupo a partir do nome (ex: "Grupo A" → "A", "Group H" → "H")
function extractGroupLetter(name: string): string {
  const m = name.match(/[A-Z]$/);
  return m ? m[0] : name.slice(-1).toUpperCase();
}

// Encontra de qual grupo (letra) um time pertence
function findTeamGroupLetter(team: SimTeam, groups: { name: string; teams: SimTeam[] }[]): string | null {
  for (const g of groups) {
    if (g.teams.find(t => t.id === team.id)) return extractGroupLetter(g.name);
  }
  return null;
}

// FIFA 2026: tabela oficial de elegibilidade dos 3°s colocados.
// Cada slot (1° do grupo X) só pode enfrentar 3°s de grupos específicos.
const ELIGIBLE_THIRDS: Record<string, string[]> = {
  E: ['A', 'B', 'C', 'D', 'F'], // M74: 1E vs 3°
  I: ['C', 'D', 'F', 'G', 'H'], // M77: 1I vs 3°
  A: ['C', 'E', 'F', 'H', 'I'], // M79: 1A vs 3°
  L: ['E', 'H', 'I', 'J', 'K'], // M80: 1L vs 3°
  D: ['B', 'E', 'F', 'I', 'J'], // M81: 1D vs 3°
  G: ['A', 'E', 'H', 'I', 'J'], // M82: 1G vs 3°
  B: ['E', 'F', 'G', 'I', 'J'], // M85: 1B vs 3°
  K: ['D', 'E', 'I', 'J', 'L'], // M87: 1K vs 3°
};

// Atribui melhores terceiros aos slots usando tabela oficial FIFA (backtracking)
function assignThirdsToSlots(
  bestThirds: SimTeam[],
  groups: { name: string; teams: SimTeam[] }[],
): Map<string, SimTeam> {
  const thirdByLetter = new Map<string, SimTeam>();
  for (const t of bestThirds) {
    const letter = findTeamGroupLetter(t, groups);
    if (letter) thirdByLetter.set(letter, t);
  }

  const qualifyingLetters = Array.from(thirdByLetter.keys()).sort();
  const slotWinners = ['E', 'I', 'A', 'L', 'D', 'G', 'B', 'K'];

  const assignment = new Map<string, string>();
  const used = new Set<string>();

  function solve(index: number): boolean {
    if (index === slotWinners.length) return true;
    const winner = slotWinners[index];
    const allowedGroups = ELIGIBLE_THIRDS[winner] ?? [];
    const eligible = qualifyingLetters.filter(g => allowedGroups.includes(g) && !used.has(g));
    for (const letter of eligible) {
      used.add(letter);
      assignment.set(winner, letter);
      if (solve(index + 1)) return true;
      used.delete(letter);
      assignment.delete(winner);
    }
    return false;
  }

  solve(0);

  const result = new Map<string, SimTeam>();
  for (const [winner, letter] of assignment) {
    const team = thirdByLetter.get(letter);
    if (team) result.set(winner, team);
  }
  return result;
}

// FIFA 2026: chaveamento oficial (48 times, 12 grupos).
// Ordem dos confrontos garante bracket correto até a final via propagação sequencial.
function generateFirstRoundFIFA2026(
  groupSel: SimTeam[][],
  bestThirds: SimTeam[],
  groups: { name: string; teams: SimTeam[] }[],
): BracketMatch[] {
  const gi: Record<string, number> = {};
  groups.forEach((g, i) => { gi[extractGroupLetter(g.name)] = i; });

  const first = (letter: string) => groupSel[gi[letter]]?.[0] ?? null;
  const second = (letter: string) => groupSel[gi[letter]]?.[1] ?? null;

  const thirdAssignment = assignThirdsToSlots(bestThirds, groups);
  const third = (slotWinner: string) => thirdAssignment.get(slotWinner) ?? null;

  return [
    // --- Chave esquerda ---
    { id: 'r0-0',  team1: first('E'),  team2: third('E'),  winner: null }, // M74: 1E vs 3°
    { id: 'r0-1',  team1: first('I'),  team2: third('I'),  winner: null }, // M77: 1I vs 3°
    { id: 'r0-2',  team1: second('A'), team2: second('B'), winner: null }, // M73: 2A vs 2B
    { id: 'r0-3',  team1: first('F'),  team2: second('C'), winner: null }, // M75: 1F vs 2C
    { id: 'r0-4',  team1: second('K'), team2: second('L'), winner: null }, // M83: 2K vs 2L
    { id: 'r0-5',  team1: first('H'),  team2: second('J'), winner: null }, // M84: 1H vs 2J
    { id: 'r0-6',  team1: first('D'),  team2: third('D'),  winner: null }, // M81: 1D vs 3°
    { id: 'r0-7',  team1: first('G'),  team2: third('G'),  winner: null }, // M82: 1G vs 3°
    // --- Chave direita ---
    { id: 'r0-8',  team1: first('C'),  team2: second('F'), winner: null }, // M76: 1C vs 2F
    { id: 'r0-9',  team1: second('E'), team2: second('I'), winner: null }, // M78: 2E vs 2I
    { id: 'r0-10', team1: first('A'),  team2: third('A'),  winner: null }, // M79: 1A vs 3°
    { id: 'r0-11', team1: first('L'),  team2: third('L'),  winner: null }, // M80: 1L vs 3°
    { id: 'r0-12', team1: first('J'),  team2: second('H'), winner: null }, // M86: 1J vs 2H
    { id: 'r0-13', team1: second('D'), team2: second('G'), winner: null }, // M88: 2D vs 2G
    { id: 'r0-14', team1: first('B'),  team2: third('B'),  winner: null }, // M85: 1B vs 3°
    { id: 'r0-15', team1: first('K'),  team2: third('K'),  winner: null }, // M87: 1K vs 3°
  ];
}

// CONMEBOL Libertadores: bracket fixo QF A×H, B×G, C×F, D×E; SF (A/H)×(D/E), (B/G)×(C/F).
// Ordem dos confrontos garante bracket correto via propagação sequencial.
function generateFirstRoundCONMEBOL(groupSel: SimTeam[][]): BracketMatch[] {
  const first = (i: number) => groupSel[i]?.[0] ?? null;
  const second = (i: number) => groupSel[i]?.[1] ?? null;

  return [
    { id: 'r0-0', team1: first(0), team2: second(1), winner: null }, // Chave A: 1°A vs 2°B
    { id: 'r0-1', team1: first(7), team2: second(6), winner: null }, // Chave H: 1°H vs 2°G
    { id: 'r0-2', team1: first(6), team2: second(7), winner: null }, // Chave D: 1°G vs 2°H
    { id: 'r0-3', team1: first(1), team2: second(0), winner: null }, // Chave E: 1°B vs 2°A
    { id: 'r0-4', team1: first(2), team2: second(3), winner: null }, // Chave B: 1°C vs 2°D
    { id: 'r0-5', team1: first(5), team2: second(4), winner: null }, // Chave G: 1°F vs 2°E
    { id: 'r0-6', team1: first(4), team2: second(5), winner: null }, // Chave C: 1°E vs 2°F
    { id: 'r0-7', team1: first(3), team2: second(2), winner: null }, // Chave F: 1°D vs 2°C
  ];
}

// CONMEBOL Sudamericana — Playoffs: 2°s da Suda vs 3°s da Libertadores.
// Emparelhamento por ranking inverso (melhor 2° Suda vs pior 3° Liberta).
function generatePlayoffsSudamericana(
  groupSel: SimTeam[][],     // Seleções dos grupos da Suda (cada [0]=1°, [1]=2°)
  libertaThirds: SimTeam[],  // 3°s da Libertadores (8 times, na ordem dos grupos)
): BracketMatch[] {
  const seconds = groupSel.map(sel => sel[1] ?? null);
  const thirdsReversed = [...libertaThirds].reverse();

  const matches: BracketMatch[] = [];
  for (let i = 0; i < 8; i++) {
    matches.push({
      id: `r0-${i}`,
      team1: seconds[i],                 // melhor 2° Suda primeiro
      team2: thirdsReversed[i] ?? null,  // pior 3° Liberta primeiro
      winner: null,
    });
  }
  return matches;
}

// CONMEBOL Sudamericana — Oitavas: 1°s da Suda vs vencedores dos Playoffs (bracket CONMEBOL).
function generateOitavasSudamericana(
  groupSel: SimTeam[][],
  playoffWinners: SimTeam[],
): BracketMatch[] {
  const firsts = groupSel.map(sel => sel[0] ?? null);
  return [
    { id: 'r1-0', team1: firsts[0], team2: playoffWinners[7] ?? null, winner: null }, // A: 1°A vs PO-H
    { id: 'r1-1', team1: firsts[7], team2: playoffWinners[0] ?? null, winner: null }, // H: 1°H vs PO-A
    { id: 'r1-2', team1: firsts[6], team2: playoffWinners[1] ?? null, winner: null }, // D: 1°G vs PO-B
    { id: 'r1-3', team1: firsts[1], team2: playoffWinners[6] ?? null, winner: null }, // E: 1°B vs PO-G
    { id: 'r1-4', team1: firsts[2], team2: playoffWinners[5] ?? null, winner: null }, // B: 1°C vs PO-F
    { id: 'r1-5', team1: firsts[5], team2: playoffWinners[2] ?? null, winner: null }, // G: 1°F vs PO-C
    { id: 'r1-6', team1: firsts[4], team2: playoffWinners[3] ?? null, winner: null }, // C: 1°E vs PO-D
    { id: 'r1-7', team1: firsts[3], team2: playoffWinners[4] ?? null, winner: null }, // F: 1°D vs PO-E
  ];
}

function generateFirstRound(
  format: string,
  groupSel: SimTeam[][],
  bestThirds: SimTeam[],
  groups?: { name: string; teams: SimTeam[] }[],
): BracketMatch[] {
  // Copa do Mundo 2026: chaveamento oficial FIFA com tabela de elegibilidade dos 3°s
  if (format === 'WORLD_CUP_48' && groups) {
    return generateFirstRoundFIFA2026(groupSel, bestThirds, groups);
  }

  // CUP_32 com 8 grupos: bracket CONMEBOL fixo (Libertadores)
  if (groupSel.length === 8) {
    return generateFirstRoundCONMEBOL(groupSel);
  }

  // Fallback genérico: crossover sequencial 1° vs 2° de outro grupo
  const firsts = groupSel.map(g => g[0] ?? null);
  const seconds = groupSel.map(g => g[1] ?? null);
  const matches: BracketMatch[] = [];
  const n = firsts.length;
  for (let i = 0; i < n; i += 2) {
    matches.push({ id: `r0-${matches.length}`, team1: firsts[i], team2: i + 1 < n ? seconds[i + 1] : null, winner: null });
    if (i + 1 < n) matches.push({ id: `r0-${matches.length}`, team1: firsts[i + 1], team2: seconds[i], winner: null });
  }
  return matches;
}

// ─── Flag component (uses logoUrl as fallback) ────────────────────────────────

function Flag({ team, className = '' }: { team: SimTeam; className?: string }) {
  const imgUrl = team.flagUrl || team.logoUrl;
  if (imgUrl) {
    return (
      <img
        src={imgUrl}
        alt={team.name}
        className={`object-contain rounded-sm flex-shrink-0 ${className}`}
        loading="lazy"
      />
    );
  }
  return (
    <div className={`bg-gray-300 rounded-sm flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0 ${className}`}>
      {team.code3.slice(0, 2)}
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ step, isCopa, isSudamericana, sk }: {
  step: Step; isCopa: boolean; isSudamericana: boolean; sk: Sk;
}) {
  const all = [
    { id: 'groups', label: sk('step_groups', 'Grupos') },
    ...(isCopa ? [{ id: 'best_thirds', label: sk('step_thirds', '3ºs') }] : []),
    ...(isSudamericana ? [{ id: 'liberta_thirds', label: sk('step_liberta_thirds', '3ºs Liberta') }] : []),
    { id: 'bracket', label: sk('step_knockout', 'Mata-Mata') },
    { id: 'champion', label: sk('step_champion', 'Campeão') },
  ];
  const current = all.findIndex(s => s.id === step);
  return (
    <div className="flex items-end justify-center gap-0 mb-8">
      {all.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={s.id} className="flex items-center">
            {i > 0 && <div className={`w-6 sm:w-10 h-0.5 mb-4 ${done || active ? 'bg-primary-400' : 'bg-slate-600'}`} />}
            <div className="flex flex-col items-center gap-1.5">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                active ? 'bg-primary-600 text-white ring-4 ring-primary-900 scale-110' :
                done ? 'bg-primary-500 text-white' : 'bg-slate-700 text-slate-400'
              }`}>
                {done ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs font-medium whitespace-nowrap ${
                active ? 'text-primary-400 font-semibold' : done ? 'text-primary-500' : 'text-slate-500'
              }`}>{s.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tree match card (compact, for bracket tree view) ────────────────────────

function TreeMatchCard({ match, isActive, onPickWinner, isRightSide, toDefineLabel }: {
  match: BracketMatch;
  isActive: boolean;
  onPickWinner: (matchId: string, team: SimTeam) => void;
  isRightSide?: boolean;
  toDefineLabel: string;
}) {
  const decided = match.winner !== null;
  return (
    <div className={`rounded-lg overflow-hidden border shadow-sm ${
      isActive && !decided ? 'border-primary-500 bg-slate-700' :
      decided ? 'border-green-700/50 bg-slate-700' :
      'border-slate-600 bg-slate-700/50'
    }`}>
      {([match.team1, match.team2] as (SimTeam | null)[]).map((team, ti) => {
        const isWinner = decided && match.winner?.id === team?.id;
        const isLost = decided && !isWinner;
        if (!team) return (
          <div key={ti} className={`flex items-center gap-1.5 px-2 py-1.5 ${isRightSide ? 'flex-row-reverse' : ''} ${ti === 0 ? 'border-b border-slate-600' : ''}`}>
            <div className="w-5 h-3 bg-slate-600 rounded-sm flex-shrink-0" />
            <span className="text-xs text-slate-500 italic">{toDefineLabel}</span>
          </div>
        );
        return (
          <button key={team.id}
            onClick={() => { if (isActive) onPickWinner(match.id, team); }}
            disabled={!isActive}
            className={`w-full flex items-center gap-1.5 px-2 py-1.5 transition-all ${
              isRightSide ? 'flex-row-reverse' : 'text-left'
            } ${
              ti === 0 ? 'border-b border-slate-600' : ''
            } ${
              isWinner ? 'bg-green-900/40' :
              isLost ? 'opacity-40 bg-slate-700' :
              isActive ? 'hover:bg-primary-900/30 cursor-pointer' :
              'cursor-default'
            }`}>
            <Flag team={team} className="w-5 h-3.5 flex-shrink-0" />
            <span className={`text-xs font-medium truncate flex-1 ${isRightSide ? 'text-right' : ''} ${
              isWinner ? 'text-green-300' : isLost ? 'text-slate-500' : 'text-slate-200'
            }`}>{getTeamName(team)}</span>
            {isWinner && (isRightSide
              ? <ChevronLeft className="w-3 h-3 text-green-400 flex-shrink-0" />
              : <ChevronRight className="w-3 h-3 text-green-400 flex-shrink-0" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Hook: detecta tela mobile ────────────────────────────────────────────────

function useIsMobile() {
  const [mobile, setMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mobile;
}

// ─── Bracket tree view (horizontal, mirrored — left half | Final | right half) ─

// Sudamericana bracket: mapeia posição da Oitavas → posição do Playoff que a alimenta.
// Derivado de generateOitavasSudamericana: cada Oit_X recebe 1° da Suda + vencedor de PO específico.
// Exemplo: Oit_A (rounds[1][0]) recebe vencedor de PO m7 (rounds[0][7]).
const SUDA_OIT_TO_PO = [7, 0, 1, 6, 5, 2, 3, 4];

function BracketTreeView({ rounds, roundNames, curRound, onPickWinner, isSudamericana, toDefineLabel }: {
  rounds: BracketMatch[][];
  roundNames: string[];
  curRound: number;
  onPickWinner: (matchId: string, team: SimTeam) => void;
  isSudamericana: boolean;
  toDefineLabel: string;
}) {
  const isMobile = useIsMobile();
  const CARD_W = isMobile ? 110 : 160;
  const CARD_H = isMobile ? 54 : 58;
  const COL_GAP = isMobile ? 12 : 18;
  const COL_W = CARD_W + COL_GAP;
  const BASE_SLOT_H = isMobile ? 70 : 76;
  const HEADER_H = 32;
  const LINE_COLOR = '#334155';
  const WIN_COLOR = '#22c55e';

  // Side rounds = all rounds except the Final — based on roundNames, not rounds filled so far
  const numSideRounds = Math.max(0, roundNames.length - 1);
  const firstRoundCount = rounds[0]?.length ?? 0;
  // halfCount: matches in each half of the first round
  const expectedHalf = numSideRounds > 0 ? Math.pow(2, numSideRounds - 1) : 0;
  const halfCount = Math.max(Math.ceil(firstRoundCount / 2), expectedHalf);

  const TOTAL_H = Math.max(halfCount * BASE_SLOT_H, 200);
  const finalColX = numSideRounds * COL_W;
  const rightStart = (numSideRounds + 1) * COL_W; // after Final col
  const totalW = numSideRounds * 2 * COL_W + CARD_W;

  const finalCardH = Math.max(CARD_H * 2 + 4, 80);
  const finalCardY = Math.max(HEADER_H, HEADER_H + TOTAL_H / 2 - finalCardH / 2);
  const finalCenterY = HEADER_H + TOTAL_H / 2;

  function getSlotH(ri: number) { return BASE_SLOT_H * Math.pow(2, ri); }
  function getCardY(ri: number, mi: number) {
    const slotH = getSlotH(ri);
    return HEADER_H + mi * slotH + (slotH - CARD_H) / 2;
  }

  function renderPlaceholder(height: number) {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-800/50 flex items-center justify-center" style={{ height }}>
        <span className="text-xs text-slate-700">—</span>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto -mx-4 sm:-mx-6 pb-2">
      <div className="px-4 sm:px-6">
        <div className="relative" style={{ width: totalW, height: TOTAL_H + HEADER_H + 8 }}>

          {/* ── Final column header ── */}
          <div style={{ position: 'absolute', left: finalColX, top: 0, width: CARD_W, textAlign: 'center' }}>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-600 text-white whitespace-nowrap">
              {roundNames[roundNames.length - 1] ?? 'Final'}
            </span>
          </div>

          {/* ── Final match card (center, double height) ── */}
          {(() => {
            const finalMatch = rounds[roundNames.length - 1]?.[0];
            const isFinalActive = curRound === roundNames.length - 1;
            return (
              <div style={{ position: 'absolute', left: finalColX, top: finalCardY, width: CARD_W }}>
                {finalMatch
                  ? <TreeMatchCard match={finalMatch} isActive={isFinalActive} onPickWinner={onPickWinner} toDefineLabel={toDefineLabel} />
                  : renderPlaceholder(finalCardH)}
              </div>
            );
          })()}

          {/* ── Left side rounds (outer → inner → Final) ── */}
          {Array.from({ length: numSideRounds }, (_, r) => {
            const round = rounds[r] ?? [];
            const leftMatches = round.slice(0, Math.ceil(round.length / 2));
            const leftCount = Math.max(1, Math.round(halfCount / Math.pow(2, r)));
            const isActive = r === curRound;
            const isPast = r < curRound;
            const colX = r * COL_W;
            const elbowX = colX + CARD_W + COL_GAP / 2;
            const isLastSide = r === numSideRounds - 1;

            return (
              <Fragment key={`L${r}`}>
                {/* Round header */}
                <div style={{ position: 'absolute', left: colX, top: 0, width: CARD_W, textAlign: 'center' }}>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                    isActive ? 'bg-primary-600 text-white' : isPast ? 'text-primary-400' : 'text-slate-500'
                  }`}>{roundNames[r] ?? ''}</span>
                </div>

                {/* Sudamericana: PO column custom — 4 cards alinhados com Oitavas (sem placeholders extras) */}
                {r === 0 && isSudamericana ? (
                  Array.from({ length: 4 }, (_, mi) => {
                    const poIdx = SUDA_OIT_TO_PO[mi];
                    const match = rounds[0]?.[poIdx];
                    const cardY = getCardY(1, mi); // alinha com Oitavas (nível 1)
                    const cardCenterY = cardY + CARD_H / 2;
                    const stubColor = match?.winner ? WIN_COLOR : LINE_COLOR;

                    return (
                      <Fragment key={`L0-suda-${mi}`}>
                        <div style={{ position: 'absolute', left: colX, top: cardY, width: CARD_W }}>
                          {match
                            ? <TreeMatchCard match={match} isActive={isActive} onPickWinner={onPickWinner} toDefineLabel={toDefineLabel} />
                            : renderPlaceholder(CARD_H)}
                        </div>
                        {/* Connector horizontal direto: PO → Oitavas (mesma altura) */}
                        <div style={{ position: 'absolute', left: colX + CARD_W, top: cardCenterY - 0.5, width: COL_GAP, height: 1, backgroundColor: stubColor }} />
                      </Fragment>
                    );
                  })
                ) : Array.from({ length: leftCount }, (_, mi) => {
                  const match = leftMatches[mi];
                  const cardY = getCardY(r, mi);
                  const cardCenterY = cardY + CARD_H / 2;
                  const isFirstInPair = mi % 2 === 0;
                  const stubColor = match?.winner ? WIN_COLOR : LINE_COLOR;

                  return (
                    <Fragment key={`L${r}-${mi}`}>
                      <div style={{ position: 'absolute', left: colX, top: cardY, width: CARD_W }}>
                        {match
                          ? <TreeMatchCard match={match} isActive={isActive} onPickWinner={onPickWinner} toDefineLabel={toDefineLabel} />
                          : renderPlaceholder(CARD_H)}
                      </div>

                      {/* Horizontal stub: card right → elbow */}
                      <div style={{ position: 'absolute', left: colX + CARD_W, top: cardCenterY - 0.5, width: COL_GAP / 2, height: 1, backgroundColor: stubColor }} />

                      {/* Pair connector (drawn by first of pair) */}
                      {isFirstInPair && mi + 1 < leftCount && (() => {
                        const sibCY = getCardY(r, mi + 1) + CARD_H / 2;
                        const sibMatch = leftMatches[mi + 1];
                        const sibColor = sibMatch?.winner ? WIN_COLOR : LINE_COLOR;
                        const parentY = isLastSide
                          ? finalCenterY
                          : getCardY(r + 1, Math.floor(mi / 2)) + CARD_H / 2;
                        const nextX = isLastSide ? finalColX : colX + COL_W;
                        const elbowColor = (match?.winner || sibMatch?.winner) ? WIN_COLOR : LINE_COLOR;
                        return (
                          <>
                            {/* Vertical top: do mi=0 até parentY */}
                            <div style={{ position: 'absolute', left: elbowX - 0.5, top: cardCenterY, width: 1, height: parentY - cardCenterY, backgroundColor: stubColor }} />
                            {/* Vertical bottom: de parentY até mi=1 */}
                            <div style={{ position: 'absolute', left: elbowX - 0.5, top: parentY, width: 1, height: sibCY - parentY, backgroundColor: sibColor }} />
                            {/* Horizontal: elbow → next column */}
                            <div style={{ position: 'absolute', left: elbowX, top: parentY - 0.5, width: nextX - elbowX, height: 1, backgroundColor: elbowColor }} />
                          </>
                        );
                      })()}
                    </Fragment>
                  );
                })}
              </Fragment>
            );
          })}

          {/* ── Right side rounds (outer → inner ← mirrored) ── */}
          {Array.from({ length: numSideRounds }, (_, r) => {
            const round = rounds[r] ?? [];
            const rightMatches = round.slice(Math.ceil(round.length / 2));
            const rightCount = Math.max(1, Math.round(halfCount / Math.pow(2, r)));
            const isActive = r === curRound;
            const isPast = r < curRound;
            // r=0 is outermost (far right), r=numSideRounds-1 is innermost (just right of Final)
            const colX = rightStart + (numSideRounds - 1 - r) * COL_W;
            const elbowX = colX - COL_GAP / 2;
            const isLastSide = r === numSideRounds - 1;

            return (
              <Fragment key={`R${r}`}>
                {/* Round header (same label as left side) */}
                <div style={{ position: 'absolute', left: colX, top: 0, width: CARD_W, textAlign: 'center' }}>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                    isActive ? 'bg-primary-600 text-white' : isPast ? 'text-primary-400' : 'text-slate-500'
                  }`}>{roundNames[r] ?? ''}</span>
                </div>

                {/* Sudamericana: PO column custom (lado direito) — 4 cards alinhados com Oitavas */}
                {r === 0 && isSudamericana ? (
                  Array.from({ length: 4 }, (_, mi) => {
                    const poIdx = SUDA_OIT_TO_PO[mi + 4]; // SUDA_OIT_TO_PO[4..7] alimenta lado direito
                    const match = rounds[0]?.[poIdx];
                    const cardY = getCardY(1, mi); // alinha com Oitavas (nível 1)
                    const cardCenterY = cardY + CARD_H / 2;
                    const stubColor = match?.winner ? WIN_COLOR : LINE_COLOR;

                    return (
                      <Fragment key={`R0-suda-${mi}`}>
                        <div style={{ position: 'absolute', left: colX, top: cardY, width: CARD_W }}>
                          {match
                            ? <TreeMatchCard match={match} isActive={isActive} onPickWinner={onPickWinner} isRightSide toDefineLabel={toDefineLabel} />
                            : renderPlaceholder(CARD_H)}
                        </div>
                        {/* Connector horizontal direto: PO ← Oitavas (mesma altura, lado direito vai pra esquerda) */}
                        <div style={{ position: 'absolute', left: colX - COL_GAP, top: cardCenterY - 0.5, width: COL_GAP, height: 1, backgroundColor: stubColor }} />
                      </Fragment>
                    );
                  })
                ) : Array.from({ length: rightCount }, (_, mi) => {
                  const match = rightMatches[mi];
                  const cardY = getCardY(r, mi);
                  const cardCenterY = cardY + CARD_H / 2;
                  const isFirstInPair = mi % 2 === 0;
                  const stubColor = match?.winner ? WIN_COLOR : LINE_COLOR;

                  return (
                    <Fragment key={`R${r}-${mi}`}>
                      <div style={{ position: 'absolute', left: colX, top: cardY, width: CARD_W }}>
                        {match
                          ? <TreeMatchCard match={match} isActive={isActive} onPickWinner={onPickWinner} isRightSide toDefineLabel={toDefineLabel} />
                          : renderPlaceholder(CARD_H)}
                      </div>

                      {/* Horizontal stub: elbow → card left */}
                      <div style={{ position: 'absolute', left: elbowX, top: cardCenterY - 0.5, width: COL_GAP / 2, height: 1, backgroundColor: stubColor }} />

                      {/* Pair connector (drawn by first of pair) */}
                      {isFirstInPair && mi + 1 < rightCount && (() => {
                        const sibCY = getCardY(r, mi + 1) + CARD_H / 2;
                        const sibMatch = rightMatches[mi + 1];
                        const sibColor = sibMatch?.winner ? WIN_COLOR : LINE_COLOR;
                        const parentY = isLastSide
                          ? finalCenterY
                          : getCardY(r + 1, Math.floor(mi / 2)) + CARD_H / 2;
                        const prevX = isLastSide ? finalColX + CARD_W : colX - COL_GAP;
                        const elbowColor = (match?.winner || sibMatch?.winner) ? WIN_COLOR : LINE_COLOR;
                        return (
                          <>
                            {/* Vertical top: do mi=0 até parentY */}
                            <div style={{ position: 'absolute', left: elbowX - 0.5, top: cardCenterY, width: 1, height: parentY - cardCenterY, backgroundColor: stubColor }} />
                            {/* Vertical bottom: de parentY até mi=1 */}
                            <div style={{ position: 'absolute', left: elbowX - 0.5, top: parentY, width: 1, height: sibCY - parentY, backgroundColor: sibColor }} />
                            {/* Horizontal: prev col → elbow */}
                            <div style={{ position: 'absolute', left: prevX, top: parentY - 0.5, width: elbowX - prevX, height: 1, backgroundColor: elbowColor }} />
                          </>
                        );
                      })()}
                    </Fragment>
                  );
                })}
              </Fragment>
            );
          })}

        </div>
      </div>
    </div>
  );
}

// ─── Share image generator (Canvas API) ──────────────────────────────────────

function generateShareImage(
  bracket: SimulatorBracket,
  groupSel: SimTeam[][],
  bestThirds: SimTeam[],
  rounds: BracketMatch[][],
  roundNames: string[],
  champion: SimTeam,
  runnerUp: SimTeam | null,
  labels: ShareImageLabels,
): Promise<Blob> {
  return (async () => {
    // ── Pré-carrega bandeiras (browser cache = quase instantâneo) ──
    const allUrls = new Set<string>();
    const collectUrl = (t: SimTeam | null | undefined) => {
      const url = t?.flagUrl || t?.logoUrl;
      if (url) allUrls.add(url);
    };
    groupSel.forEach(sel => sel.forEach(collectUrl));
    bracket.groups.forEach(g => g.teams.forEach(collectUrl)); // inclui times eliminados
    bestThirds.forEach(collectUrl);
    rounds.forEach(r => r.forEach(m => { collectUrl(m.team1); collectUrl(m.team2); }));

    const imgCache = new Map<string, HTMLImageElement>();
    await Promise.allSettled(
      [...allUrls].map(url => new Promise<void>(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { imgCache.set(url, img); resolve(); };
        img.onerror = () => resolve(); // fallback: skip image, usa só texto
        img.src = url;
      }))
    );

    const PAD = 24;
    const COL_GAP = 12;
    const MATCH_GAP = 10;
    const SECTION_H = 44;
    const isCopa = bracket.format === 'WORLD_CUP_48';
    const groupCount = bracket.groups.length;
    const teamsPerGroup = isCopa ? 3 : 2;
    const maxGroupTeams = bracket.groups.reduce((m, g) => Math.max(m, g.teams.length), 4);
    const eliminatedPerGroup = Math.max(0, maxGroupTeams - teamsPerGroup);
    const GROUP_CARD_H = 14 + 26 + teamsPerGroup * 26 + eliminatedPerGroup * 20 + 18;
    // 4 colunas para Copa (12 grupos) → 3 linhas; 2 para competições menores
    const GCOLS = groupCount >= 8 ? 4 : 2;

    // ── Tree bracket dimensions ────────────────────────────────
    const numSideRounds = Math.max(0, rounds.length - 1);
    const firstRoundCount = rounds[0]?.length ?? 0;
    const halfCount = Math.floor(firstRoundCount / 2);
    const TMW = 158;
    const TMH = 46;
    const TCOL_W = TMW + 22;
    const TSLOT_H = TMH + 16;
    const TREE_H = Math.max(halfCount > 0 ? halfCount * TSLOT_H : 200, 200);

    const treeContentW = numSideRounds > 0 ? numSideRounds * 2 * TCOL_W + TMW : 0;
    const W = Math.max(1200, PAD * 4 + treeContentW);

    // ── Calculate total height ──────────────────────────────────
    let totalHeight = 90;
    if (groupCount > 0) {
      const groupRows = Math.ceil(groupCount / GCOLS);
      totalHeight += SECTION_H + groupRows * (GROUP_CARD_H + MATCH_GAP) + 8;
    }
    if (isCopa && bestThirds.length > 0) totalHeight += SECTION_H + 40;
    if (rounds.length > 0) totalHeight += SECTION_H + TREE_H + 24;
    totalHeight += 16 + 96 + 16 + 50;

    // ── Create canvas ──────────────────────────────────────────
    const dpr = Math.min(typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 2, 3);
    const canvas = document.createElement('canvas');
    canvas.width = W * dpr;
    canvas.height = totalHeight * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);

    // ── Helpers ────────────────────────────────────────────────

    function fillRR(x: number, y: number, w: number, h: number, r: number, color: string) {
      ctx.fillStyle = color;
      ctx.beginPath();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (ctx as any).roundRect === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (ctx as any).roundRect(x, y, w, h, r);
      } else {
        ctx.rect(x, y, w, h);
      }
      ctx.fill();
    }

    function truncate(text: string, maxW: number): string {
      if (ctx.measureText(text).width <= maxW) return text;
      let t = text;
      while (ctx.measureText(t + '…').width > maxW && t.length > 2) t = t.slice(0, -1);
      return t + '…';
    }

    // Desenha bandeira/logo com clip arredondado
    function drawFlag(team: SimTeam, fx: number, fy: number, fw: number, fh: number) {
      const url = team.flagUrl || team.logoUrl;
      if (!url) return;
      const img = imgCache.get(url);
      if (!img) return;
      ctx.save();
      ctx.beginPath();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (ctx as any).roundRect === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (ctx as any).roundRect(fx, fy, fw, fh, 2);
      } else {
        ctx.rect(fx, fy, fw, fh);
      }
      ctx.clip();
      ctx.drawImage(img, fx, fy, fw, fh);
      ctx.restore();
    }

    // Card compacto com bandeira + nome
    function drawTreeCard(match: BracketMatch, x: number, y: number, w: number, h: number, isRightSide = false, isFinal = false) {
      fillRR(x, y, w, h, 5, '#1e293b');
      const decided = match.winner !== null;
      const halfH = h / 2;
      const FW = 20, FH = 13;
      // Right-side cards: flag on the right edge, text right-aligned before the flag
      const flagX = isRightSide ? x + w - 8 - FW : x + 8;
      const textMaxW = isRightSide ? w - FW - 22 : w - (flagX - x) - FW - 23;

      function teamRow(team: SimTeam | null, isWinner: boolean, rowTopY: number) {
        const centerY = rowTopY + halfH / 2;
        const baseY = centerY + 4;
        const flagTop = centerY - FH / 2;
        if (!team) {
          ctx.font = 'italic 11px system-ui, sans-serif';
          ctx.fillStyle = '#64748b';
          if (isRightSide) {
            ctx.textAlign = 'right';
            ctx.fillText(labels.toDefine, x + w - 8, baseY);
            ctx.textAlign = 'left';
          } else {
            ctx.fillText(labels.toDefine, x + 8, baseY);
          }
          return;
        }
        drawFlag(team, flagX, flagTop, FW, FH);
        ctx.font = isWinner ? 'bold 12px system-ui, sans-serif' : '12px system-ui, sans-serif';
        ctx.fillStyle = isWinner ? '#f1f5f9' : decided ? '#94a3b8' : '#cbd5e1';
        if (isRightSide) {
          ctx.textAlign = 'right';
          ctx.fillText(truncate(getTeamName(team), textMaxW), flagX - 5, baseY);
          ctx.textAlign = 'left';
        } else {
          ctx.fillText(truncate(getTeamName(team), textMaxW), flagX + FW + 5, baseY);
        }
        if (isWinner) {
          ctx.fillStyle = '#4ade80';
          ctx.font = 'bold 10px system-ui, sans-serif';
          if (isFinal) {
            ctx.font = '13px system-ui, sans-serif';
            ctx.textAlign = 'right';
            ctx.fillText('🏆', x + w - 5, baseY);
            ctx.textAlign = 'left';
          } else if (isRightSide) {
            ctx.textAlign = 'left';
            ctx.fillText('◀', x + 5, baseY);
          } else {
            ctx.textAlign = 'right';
            ctx.fillText('▶', x + w - 5, baseY);
          }
          ctx.textAlign = 'left';
        }
      }

      teamRow(match.team1, match.winner?.id === match.team1?.id, y);
      ctx.fillStyle = '#334155';
      ctx.fillRect(x + 6, y + halfH - 0.5, w - 12, 1);
      teamRow(match.team2, match.winner?.id === match.team2?.id, y + halfH);
    }

    function sectionLabel(text: string, y: number): number {
      ctx.fillStyle = '#334155';
      ctx.fillRect(PAD, y, W - PAD * 2, 1);
      ctx.font = 'bold 12px system-ui, sans-serif';
      ctx.fillStyle = '#a78bfa';
      ctx.fillText(text.toUpperCase(), PAD, y + 28);
      return y + SECTION_H;
    }

    // ── Background ─────────────────────────────────────────────
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, W, totalHeight);

    // ── Header ─────────────────────────────────────────────────
    fillRR(0, 0, W, 90, 0, '#1e293b');
    ctx.font = 'bold 22px system-ui, sans-serif';
    ctx.fillStyle = '#ffffff';
    const titleName = bracket.competitionName.toUpperCase();
    const titleYear = (isCopa && !titleName.includes('2026')) ? ' 2026' : '';
    ctx.fillText(labels.headerPrefix + '  ' + titleName + titleYear, PAD, 57);

    let y = 90;

    // ── Grupos (GCOLS por linha, com bandeiras) ─────────────────
    if (groupCount > 0) {
      y = sectionLabel(labels.groupStage, y);
      const colW = (W - PAD * 2 - COL_GAP * (GCOLS - 1)) / GCOLS;
      const groupRows = Math.ceil(groupCount / GCOLS);
      const GFW = 17, GFH = 11;

      for (let row = 0; row < groupRows; row++) {
        for (let col = 0; col < GCOLS; col++) {
          const gi = row * GCOLS + col;
          if (gi >= groupCount) break;
          const group = bracket.groups[gi];
          const sel = groupSel[gi] ?? [];
          const gx = PAD + col * (colW + COL_GAP);

          fillRR(gx, y, colW, GROUP_CARD_H, 8, '#1e293b');
          ctx.font = 'bold 12px system-ui, sans-serif';
          ctx.fillStyle = '#64748b';
          ctx.fillText(labels.groupName(group), gx + 12, y + 22);

          const medals = ['🥇', '🥈', '🥉'];
          const nameColors = ['#f8fafc', '#cbd5e1', '#94a3b8'];
          const fontW = ['bold', 'normal', 'normal'];

          for (let ti = 0; ti < teamsPerGroup; ti++) {
            const team = sel[ti];
            const ty = y + 14 + 26 + ti * 26;
            ctx.font = '13px system-ui, sans-serif';
            ctx.fillStyle = '#64748b';
            ctx.fillText(medals[ti], gx + 12, ty + 13);
            if (team) {
              drawFlag(team, gx + 36, ty + 1, GFW, GFH);
              ctx.font = `${fontW[ti]} 12px system-ui, sans-serif`;
              ctx.fillStyle = nameColors[ti];
              ctx.fillText(truncate(getTeamName(team), colW - 76), gx + 36 + GFW + 4, ty + 13);
            } else {
              ctx.font = 'italic 12px system-ui, sans-serif';
              ctx.fillStyle = '#475569';
              ctx.fillText('—', gx + 36, ty + 13);
            }
          }

          // Eliminated teams in dim gray (no medal)
          const eliminated = group.teams.filter(t => !sel.some(s => s.id === t.id));
          for (let ei = 0; ei < eliminated.length; ei++) {
            const team = eliminated[ei];
            const ty = y + 14 + 26 + teamsPerGroup * 26 + ei * 20;
            ctx.globalAlpha = 0.5;
            drawFlag(team, gx + 36, ty + 1, GFW, GFH - 1);
            ctx.globalAlpha = 1;
            ctx.font = '11px system-ui, sans-serif';
            ctx.fillStyle = '#94a3b8';
            ctx.fillText(truncate(getTeamName(team), colW - 76), gx + 36 + GFW + 4, ty + 12);
          }
        }
        y += GROUP_CARD_H + MATCH_GAP;
      }
      y += 8;
    }

    // ── Melhores Terceiros ─────────────────────────────────────
    if (isCopa && bestThirds.length > 0) {
      y = sectionLabel(labels.bestThirds, y);
      const BFW = 20, BFH = 13;
      ctx.font = '12px system-ui, sans-serif';
      let tx = PAD;
      const ty = y + 4;
      for (const t of bestThirds) {
        const name = getTeamName(t);
        drawFlag(t, tx, ty + 1, BFW, BFH);
        ctx.fillStyle = '#94a3b8';
        ctx.fillText(name, tx + BFW + 4, ty + BFH);
        tx += BFW + 4 + ctx.measureText(name).width + 14;
      }
      y += 40;
    }

    // ── Bracket tree (horizontal, espelhado) ───────────────────
    if (rounds.length > 0) {
      y = sectionLabel(labels.knockout, y);
      const treeTop = y;
      const treeW = numSideRounds * 2 * TCOL_W + TMW;
      const treeLeft = Math.max(PAD, Math.floor((W - treeW) / 2));
      const finalColX = treeLeft + numSideRounds * TCOL_W;
      const rightStart = finalColX + TCOL_W;

      for (let r = 0; r < numSideRounds; r++) {
        const round = rounds[r];
        const leftMatches = round.slice(0, Math.ceil(round.length / 2));
        const rightMatches = round.slice(Math.ceil(round.length / 2));
        const slotsInHalf = halfCount / Math.pow(2, r);
        const slotH = TREE_H / slotsInHalf;
        const isLastSide = r === numSideRounds - 1;

        leftMatches.forEach((match, mi) => {
          const mx = treeLeft + r * TCOL_W;
          const my = treeTop + mi * slotH + (slotH - TMH) / 2;
          drawTreeCard(match, mx, my, TMW, TMH);

          const elbowX = mx + TMW + 8;
          const myCY = my + TMH / 2;
          ctx.strokeStyle = '#334155';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(mx + TMW, myCY);
          ctx.lineTo(elbowX, myCY);
          ctx.stroke();

          if (mi % 2 === 0 && mi + 1 < leftMatches.length) {
            const siblCY = treeTop + (mi + 1) * slotH + (slotH - TMH) / 2 + TMH / 2;
            const parentSlotH = isLastSide ? TREE_H : TREE_H / (slotsInHalf / 2);
            const parentMi = Math.floor(mi / 2);
            const parentCY = isLastSide
              ? treeTop + TREE_H / 2
              : treeTop + parentMi * parentSlotH + (parentSlotH - TMH) / 2 + TMH / 2;
            const parentX = isLastSide ? finalColX : mx + TCOL_W;
            ctx.beginPath(); ctx.moveTo(elbowX, myCY); ctx.lineTo(elbowX, siblCY); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(elbowX, parentCY); ctx.lineTo(parentX, parentCY); ctx.stroke();
          }
        });

        rightMatches.forEach((match, mi) => {
          const mx = rightStart + (numSideRounds - 1 - r) * TCOL_W;
          const my = treeTop + mi * slotH + (slotH - TMH) / 2;
          drawTreeCard(match, mx, my, TMW, TMH, true);

          const elbowX = mx - 8;
          const myCY = my + TMH / 2;
          ctx.strokeStyle = '#334155';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(mx, myCY);
          ctx.lineTo(elbowX, myCY);
          ctx.stroke();

          if (mi % 2 === 0 && mi + 1 < rightMatches.length) {
            const siblCY = treeTop + (mi + 1) * slotH + (slotH - TMH) / 2 + TMH / 2;
            const parentSlotH = isLastSide ? TREE_H : TREE_H / (slotsInHalf / 2);
            const parentMi = Math.floor(mi / 2);
            const parentCY = isLastSide
              ? treeTop + TREE_H / 2
              : treeTop + parentMi * parentSlotH + (parentSlotH - TMH) / 2 + TMH / 2;
            const parentRX = isLastSide ? finalColX + TMW : mx - (TCOL_W - TMW);
            ctx.beginPath(); ctx.moveTo(elbowX, myCY); ctx.lineTo(elbowX, siblCY); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(elbowX, parentCY); ctx.lineTo(parentRX, parentCY); ctx.stroke();
          }
        });
      }

      const finalMatch = rounds[rounds.length - 1]?.[0];
      if (finalMatch) {
        const finalH = TMH * 2 + 4;
        const finalY = treeTop + TREE_H / 2 - finalH / 2;
        drawTreeCard(finalMatch, finalColX, finalY, TMW, finalH, false, true);
        ctx.font = 'bold 10px system-ui, sans-serif';
        ctx.fillStyle = '#a78bfa';
        ctx.textAlign = 'center';
        ctx.fillText((roundNames[rounds.length - 1] ?? 'FINAL').toUpperCase(), finalColX + TMW / 2, finalY - 6);
        ctx.textAlign = 'left';
      }

      y = treeTop + TREE_H + 24;
    }

    // ── Campeão (centered, modern) ─────────────────────────────
    y += 20;
    const champBoxW = Math.min(W - PAD * 6, 520);
    const champBoxX = (W - champBoxW) / 2;
    const champBoxH = runnerUp ? 116 : 84;
    // Gold gradient box
    const champGrad = ctx.createLinearGradient(champBoxX, y, champBoxX + champBoxW, y + champBoxH);
    champGrad.addColorStop(0, '#78350f');
    champGrad.addColorStop(0.5, '#92400e');
    champGrad.addColorStop(1, '#78350f');
    ctx.fillStyle = champGrad;
    ctx.beginPath();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (ctx as any).roundRect === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (ctx as any).roundRect(champBoxX, y, champBoxW, champBoxH, 16);
    } else {
      ctx.rect(champBoxX, y, champBoxW, champBoxH);
    }
    ctx.fill();
    // "CAMPEÃO" label
    ctx.textAlign = 'center';
    ctx.font = 'bold 12px system-ui, sans-serif';
    ctx.fillStyle = '#fbbf24';
    ctx.fillText('🏆  ' + labels.champion, W / 2, y + 22);
    // Champion flag + name centered
    const champName = getTeamName(champion).toUpperCase();
    ctx.font = 'bold 22px system-ui, sans-serif';
    const champTextW = ctx.measureText(champName).width;
    const cFW = 32, cFH = 22;
    const champContentW = cFW + 8 + champTextW;
    const champStartX = (W - champContentW) / 2;
    drawFlag(champion, champStartX, y + 30, cFW, cFH);
    ctx.fillStyle = '#fef3c7';
    ctx.textAlign = 'left';
    ctx.fillText(champName, champStartX + cFW + 8, y + 48);
    if (runnerUp) {
      ctx.textAlign = 'center';
      ctx.font = '13px system-ui, sans-serif';
      ctx.fillStyle = '#fde68a';
      const runnerName = getTeamName(runnerUp);
      const rFW = 22, rFH = 15;
      const runnerContentW = rFW + 6 + ctx.measureText(runnerName).width;
      const runnerStartX = (W - runnerContentW) / 2;
      drawFlag(runnerUp, runnerStartX, y + 92, rFW, rFH);
      ctx.textAlign = 'left';
      ctx.fillText(runnerName, runnerStartX + rFW + 6, y + 105);
      ctx.textAlign = 'left';
      // Re-draw vice label to the left of flag
      ctx.font = '11px system-ui, sans-serif';
      ctx.fillStyle = '#fde68a';
      ctx.textAlign = 'center';
      ctx.fillText('🥈 ' + labels.runnerUp, W / 2, y + 85);
      ctx.textAlign = 'left';
    }
    y += champBoxH + 20;

    // ── Footer ─────────────────────────────────────────────────
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillStyle = '#475569';
    ctx.textAlign = 'center';
    ctx.fillText(labels.footer, W / 2, y + 20);
    ctx.textAlign = 'left';

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      }, 'image/png');
    });
  })();
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props { competitionExternalId: number; }

export function CupSimulator({ competitionExternalId }: Props) {
  const { t } = useTranslation('landing');
  // sk = atalho i18n do simulador (namespace 'landing', prefixo 'simulator.').
  const sk: Sk = (suffix, def, opts) => t('simulator.' + suffix, def, opts);
  // Nome do grupo traduzido via chave estável do backend (fallback: texto PT).
  const groupName = (g: { name: string; nameKey?: string }) =>
    g.nameKey ? t(g.nameKey, g.name) : g.name;

  const containerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  const [bracket, setBracket] = useState<SimulatorBracket | null>(null);
  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [shareState, setShareState] = useState<ShareState>('idle');

  const [groupSel, setGroupSel] = useState<SimTeam[][]>([]);
  const [bestThirds, setBestThirds] = useState<SimTeam[]>([]);
  // Sudamericana: 1 time selecionado por grupo da Liberta (3° colocado), totalizando 8.
  const [libertaThirds, setLibertaThirds] = useState<SimTeam[][]>([]);
  const [rounds, setRounds] = useState<BracketMatch[][]>([]);
  const [curRound, setCurRound] = useState(0);
  const [roundNames, setRoundNames] = useState<string[]>([]);
  const [champion, setChampion] = useState<SimTeam | null>(null);

  // ── Scroll to simulator top on step/round change ───────────
  useEffect(() => {
    if (!initializedRef.current) {
      if (step !== 'loading' && step !== 'error') initializedRef.current = true;
      return;
    }
    if (step === 'loading' || step === 'error') return;
    const timer = setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(timer);
  }, [step, curRound]);

  // ── Load bracket data ──────────────────────────────────────
  useEffect(() => {
    let active = true;
    initializedRef.current = false;
    setStep('loading');
    setError('');

    simulatorApi.getBracket(competitionExternalId)
      .then(data => {
        if (!active) return;
        setBracket(data);
        // Nomes de fase traduzidos via chaves do backend (fallback: texto PT).
        // O 3º lugar não entra no bracket — filtrado pela chave estável.
        const rTexts = data.rules.knockoutRoundNames ?? [];
        const rKeys = data.rules.knockoutRoundNameKeys ?? [];
        const rNames = rTexts
          .map((text, i) => ({ text, key: rKeys[i] }))
          .filter(x => (x.key ? x.key !== 'simulator.round.third' : x.text !== '3º Lugar'))
          .map(x => (x.key ? t(x.key, x.text) : x.text));
        setRoundNames(rNames);

        const saved = loadSim(competitionExternalId) as {
          step?: Step; groupSel?: SimTeam[][];
          bestThirds?: SimTeam[]; libertaThirds?: SimTeam[][];
          rounds?: BracketMatch[][];
          curRound?: number; roundNames?: string[];
          champion?: SimTeam | null;
        } | null;

        const libertaInit = data.libertadoresGroups?.map(() => []) ?? [];

        // Estado salvo é compatível só se o número de rodadas bate com o backend atual.
        // Sem isso, simulações antigas (ex.: Suda em 4 rodadas, sem Playoffs) sobrescreveriam
        // o roundNames novo e fariam o bracket pular Semifinais — QF winners propagariam
        // direto pra rounds[3] e o componente trataria isso como Final.
        const savedRoundCountOk = !saved?.roundNames || saved.roundNames.length === rNames.length;

        if (saved?.step && ['groups', 'best_thirds', 'liberta_thirds', 'bracket', 'champion'].includes(saved.step) && savedRoundCountOk) {
          const restoredGroupSel = saved.groupSel ?? [];
          setGroupSel(restoredGroupSel.length === data.groups.length ? restoredGroupSel : data.groups.map(() => []));
          setBestThirds(saved.bestThirds ?? []);
          const restoredLiberta = saved.libertaThirds ?? [];
          setLibertaThirds(restoredLiberta.length === libertaInit.length ? restoredLiberta : libertaInit);
          setRounds(saved.rounds ?? []);
          setCurRound(saved.curRound ?? 0);
          setChampion(saved.champion ?? null);
          setStep(saved.step);
        } else {
          // Estado incompatível ou inexistente: limpa pra evitar render quebrado
          try { localStorage.removeItem(`sim_${competitionExternalId}`); } catch { /* ok */ }
          setGroupSel(data.groups.map(() => []));
          setLibertaThirds(libertaInit);
          setStep('groups');
        }
      })
      .catch(() => {
        if (!active) return;
        setError(sk('error_load', 'Não foi possível carregar os dados do simulador. Tente novamente.'));
        setStep('error');
      });

    return () => { active = false; };
    // `t` nas deps: trocar de idioma recarrega o bracket e re-resolve os
    // nomes de fase; o estado da simulação é restaurado do localStorage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionExternalId, retry, t]);

  // ── Persist state ──────────────────────────────────────────
  useEffect(() => {
    if (!bracket || step === 'loading' || step === 'error') return;
    saveSim(competitionExternalId, { step, groupSel, bestThirds, libertaThirds, rounds, curRound, roundNames, champion });
  }, [bracket, step, groupSel, bestThirds, libertaThirds, rounds, curRound, roundNames, champion, competitionExternalId]);

  // ── Handlers ───────────────────────────────────────────────

  function toggleGroupTeam(gi: number, team: SimTeam) {
    if (!bracket) return;
    const max = bracket.format === 'WORLD_CUP_48' ? 3 : 2;
    setGroupSel(prev => prev.map((g, i) => {
      if (i !== gi) return g;
      const pos = g.findIndex(t => t.id === team.id);
      if (pos >= 0) { const r = [...g]; r.splice(pos, 1); return r; }
      if (g.length >= max) return g;
      return [...g, team];
    }));
  }

  function toggleBestThird(team: SimTeam) {
    setBestThirds(prev => {
      const idx = prev.findIndex(t => t.id === team.id);
      if (idx >= 0) return prev.filter(t => t.id !== team.id);
      if (prev.length >= 8) return prev;
      return [...prev, team];
    });
  }

  // Sudamericana: rodada 0 = Playoffs (não emparelha sequencialmente; gera Oitavas via bracket CONMEBOL).
  const isSudamericanaRoundIsPlayoffs = (ri: number) =>
    !!bracket?.libertadoresGroups && ri === 0;

  function goFromGroups() {
    if (!bracket) return;
    if (bracket.format === 'WORLD_CUP_48') {
      setStep('best_thirds');
    } else if (bracket.libertadoresGroups) {
      setStep('liberta_thirds');
    } else {
      const r0 = generateFirstRound(bracket.format, groupSel, [], bracket.groups);
      setRounds([r0]); setCurRound(0); setStep('bracket');
    }
  }

  function goFromBestThirds() {
    if (!bracket) return;
    const r0 = generateFirstRound(bracket.format, groupSel, bestThirds, bracket.groups);
    setRounds([r0]); setCurRound(0); setStep('bracket');
  }

  function goFromLibertaThirds() {
    if (!bracket) return;
    const thirds = libertaThirds.map(sel => sel[0]).filter(Boolean) as SimTeam[];
    const playoffs = generatePlayoffsSudamericana(groupSel, thirds);
    setRounds([playoffs]); setCurRound(0); setStep('bracket');
  }

  function toggleLibertaThird(gi: number, team: SimTeam) {
    setLibertaThirds(prev => prev.map((sel, i) => {
      if (i !== gi) return sel;
      const exists = sel.find(t => t.id === team.id);
      return exists ? [] : [team];
    }));
  }

  function pickWinner(matchId: string, winner: SimTeam) {
    setRounds(prev => {
      const curRoundMatches = prev[curRound];
      const matchIdx = curRoundMatches.findIndex(m => m.id === matchId);
      if (matchIdx === -1) return prev;

      // 1. Atualiza vencedor na rodada atual
      const updatedCur = curRoundMatches.map(m =>
        m.id === matchId ? { ...m, winner } : m
      );

      // Se for a rodada final, não há próxima
      if (curRound >= roundNames.length - 1) {
        return prev.map((r, ri) => ri === curRound ? updatedCur : r);
      }

      // Sudamericana Playoffs: NÃO propaga sequencialmente — Oitavas é gerada por advanceRound
      // com bracket CONMEBOL fixo (1°s da Suda vs vencedores dos Playoffs).
      if (isSudamericanaRoundIsPlayoffs(curRound)) {
        return prev.map((r, ri) => ri === curRound ? updatedCur : r);
      }

      // 2. Propaga vencedor imediatamente para o slot correto da próxima rodada
      const nextRoundIdx = curRound + 1;
      const nextMatchIdx = Math.floor(matchIdx / 2);
      const isTeam1 = matchIdx % 2 === 0;
      const nextRoundSize = Math.ceil(curRoundMatches.length / 2);

      const existingNext = prev[nextRoundIdx] ?? Array.from({ length: nextRoundSize }, (_, i) => ({
        id: `r${nextRoundIdx}-${i}`,
        team1: null as SimTeam | null,
        team2: null as SimTeam | null,
        winner: null as SimTeam | null,
      }));

      const updatedNext = existingNext.map((m, i) => {
        if (i !== nextMatchIdx) return m;
        // Reseta winner do próximo match quando o time que alimentava muda
        return isTeam1
          ? { ...m, team1: winner, winner: null }
          : { ...m, team2: winner, winner: null };
      });

      const result = prev.map((r, ri) => ri === curRound ? updatedCur : r);
      result[nextRoundIdx] = updatedNext;
      return result;
    });
  }

  function advanceRound() {
    if (curRound === roundNames.length - 1) {
      const w = rounds[curRound][0]?.winner;
      if (w) { setChampion(w); setStep('champion'); }
      return;
    }

    // Sudamericana: após Playoffs, gerar Oitavas com bracket CONMEBOL fixo
    if (isSudamericanaRoundIsPlayoffs(curRound)) {
      const winners = rounds[curRound].map(m => m.winner!).filter(Boolean);
      const oitavas = generateOitavasSudamericana(groupSel, winners);
      setRounds(prev => {
        const next = [...prev];
        next[curRound + 1] = oitavas;
        return next;
      });
      setCurRound(prev => prev + 1);
      return;
    }

    // Próxima rodada já está construída pelo pickWinner — só incrementa o índice
    setCurRound(prev => prev + 1);
  }

  // Auto-avanço: quando todos os confrontos da rodada atual estiverem decididos,
  // avança automaticamente após 700ms (permite visualizar o último vencedor)
  useEffect(() => {
    if (step !== 'bracket') return;
    const curMatches = rounds[curRound];
    if (!curMatches || curMatches.length === 0) return;
    if (!curMatches.every(m => m.winner !== null)) return;
    const timer = setTimeout(() => advanceRound(), 700);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rounds, curRound, step]);

  function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function randomFillGroups() {
    if (!bracket) return;
    const advanceCount = bracket.rules.teamsAdvancePerGroup;
    const hasThirdPlace = bracket.rules.bestThirdPlaces > 0;
    const newSel = bracket.groups.map((g) => shuffle(g.teams).slice(0, hasThirdPlace ? advanceCount + 1 : advanceCount));
    setGroupSel(newSel);
  }

  function randomFillBestThirds() {
    if (!bracket) return;
    const needed = bracket.rules.bestThirdPlaces;
    const thirds = groupSel.map((sel) => sel[bracket.rules.teamsAdvancePerGroup]).filter(Boolean) as SimTeam[];
    setBestThirds(shuffle(thirds).slice(0, needed));
  }

  function randomFillBracket() {
    const curRoundMatches = rounds[curRound] ?? [];
    for (const m of curRoundMatches) {
      if (m.winner) continue;
      const winner = Math.random() < 0.5 ? (m.team1 ?? m.team2!) : (m.team2 ?? m.team1!);
      pickWinner(m.id, winner);
    }
  }

  function randomFillOneGroup(gi: number) {
    if (!bracket) return;
    const advanceCount = bracket.rules.teamsAdvancePerGroup;
    const hasThirdPlace = bracket.rules.bestThirdPlaces > 0;
    const g = bracket.groups[gi];
    setGroupSel(prev => prev.map((sel, i) => {
      if (i !== gi) return sel;
      return shuffle(g.teams).slice(0, hasThirdPlace ? advanceCount + 1 : advanceCount);
    }));
  }

  function reset() {
    if (!bracket) return;
    try { localStorage.removeItem(`sim_${competitionExternalId}`); } catch { /* ok */ }
    setGroupSel(bracket.groups.map(() => []));
    setBestThirds([]);
    setLibertaThirds(bracket.libertadoresGroups?.map(() => []) ?? []);
    setRounds([]); setCurRound(0); setChampion(null); setStep('groups');
  }

  function randomFillLibertaThirds() {
    if (!bracket?.libertadoresGroups) return;
    const newSel = bracket.libertadoresGroups.map(g => [shuffle(g.teams)[0]]);
    setLibertaThirds(newSel);
  }

  async function share() {
    if (!bracket || !champion) return;
    setShareState('generating');

    try {
      const finalRound = rounds[rounds.length - 1];
      const finalMatch = finalRound?.[0];
      const runnerUp = finalMatch
        ? (finalMatch.team1?.id === champion.id ? finalMatch.team2 : finalMatch.team1)
        : null;

      const shareLabels: ShareImageLabels = {
        headerPrefix: sk('share_header_prefix', 'SIMULADOR'),
        groupStage: sk('share_group_stage', 'Fase de Grupos'),
        bestThirds: sk('share_best_thirds', 'Melhores Terceiros que Avançam'),
        knockout: sk('share_knockout', 'Mata-Mata'),
        champion: sk('share_champion', 'CAMPEÃO'),
        runnerUp: sk('runnerup_label', 'Vice-campeão'),
        footer: sk('share_footer', 'bolaoelite.com.br  ·  Faça a sua simulação!'),
        toDefine: sk('to_define', 'A definir'),
        groupName,
      };
      const blob = await generateShareImage(
        bracket, groupSel, bestThirds, rounds, roundNames, champion, runnerUp, shareLabels);
      const file = new File([blob], 'simulacao.png', { type: 'image/png' });

      if (navigator.canShare?.({ files: [file] })) {
        const year = bracket.format === 'WORLD_CUP_48' && !bracket.competitionName.includes('2026') ? ' 2026' : '';
        const compName = bracket.competitionName + year;
        const shareText = sk(
          'share_text',
          'Segue minha simulação para a {{competition}}! E a sua, como fica? Clique aqui pra simular também: {{url}}',
          { competition: compName, url: window.location.href },
        );
        await navigator.share({ files: [file], title: compName, text: shareText });
      } else {
        // Fallback: download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'simulacao.png'; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }

      setShareState('done');
      setTimeout(() => setShareState('idle'), 2500);
    } catch {
      setShareState('idle');
    }
  }

  // ── Early returns ──────────────────────────────────────────

  if (!bracket && step === 'loading') {
    return (
      <div className="flex items-center justify-center gap-3 py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-sm">{sk('loading', 'Carregando dados da competição...')}</span>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="text-center py-16 px-4">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-slate-300 mb-4 text-sm">{error}</p>
        <button onClick={() => setRetry(r => r + 1)}
          className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg transition-colors">
          {sk('retry', 'Tentar novamente')}
        </button>
      </div>
    );
  }

  if (!bracket) return null;

  const isCopa = bracket.format === 'WORLD_CUP_48';
  const isSudamericana = !!bracket.libertadoresGroups;
  const maxQual = isCopa ? 3 : 2;
  const allGroupsDone = bracket.groups.length > 0 && groupSel.every(g => g.length >= maxQual);
  const allLibertaThirdsDone =
    !isSudamericana || (bracket.libertadoresGroups?.every((_, i) => (libertaThirds[i] ?? []).length === 1) ?? false);
  const curMatches = rounds[curRound] ?? [];
  const allMatchesDone = curMatches.length > 0 && curMatches.every(m => m.winner !== null);
  const isLastRound = curRound === roundNames.length - 1;
  const completedGroups = groupSel.filter(g => g.length >= maxQual).length;
  const decidedMatches = curMatches.filter(m => m.winner).length;
  const completedLibertaGroups = libertaThirds.filter(sel => sel.length === 1).length;

  // ── Render ─────────────────────────────────────────────────

  return (
    <div ref={containerRef} className="max-w-4xl mx-auto scroll-mt-4 bg-slate-800 rounded-2xl p-4 sm:p-6 shadow-xl">
      <StepIndicator step={step} isCopa={isCopa} isSudamericana={isSudamericana} sk={sk} />

      {step !== 'groups' && step !== 'champion' && (
        <div className="flex justify-end mb-4">
          <button onClick={reset} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> {sk('restart', 'Recomeçar')}
          </button>
        </div>
      )}

      {/* ══ GRUPOS ══════════════════════════════════════════════════════════ */}
      {step === 'groups' && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-white">{sk('groups_title', 'Fase de Grupos')}</h3>
              <p className="text-sm text-slate-400 mt-0.5">
                {isCopa
                  ? sk('groups_hint_copa', 'Escolha o 1°, 2° e 3° de cada grupo')
                  : sk('groups_hint_default', 'Escolha o 1° e 2° de cada grupo')}
                {bracket.groups.length > 0 && (
                  <> &mdash; <span className="font-semibold text-primary-600">{completedGroups}/{bracket.groups.length}</span> {sk('groups_complete', 'completos')}</>
                )}
              </p>
            </div>
            {bracket.groups.length > 0 && (
              <button
                onClick={randomFillGroups}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-semibold rounded-lg transition-colors flex-shrink-0"
              >
                🎲 {sk('draw', 'Sortear')}
              </button>
            )}
          </div>

          {bracket.groups.length === 0 ? (
            <div className="text-center py-16 px-4 border-2 border-dashed border-slate-600 rounded-2xl">
              <Trophy className="w-10 h-10 text-slate-500 mx-auto mb-3" />
              <p className="font-semibold text-slate-300 mb-1">{sk('data_unavailable_title', 'Dados ainda não disponíveis')}</p>
              <p className="text-sm text-slate-400 max-w-sm mx-auto">
                {sk('data_unavailable_desc', 'Os grupos e seleções serão carregados automaticamente assim que os jogos classificatórios forem concluídos.')}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {bracket.groups.map((group, gi) => {
                  const sel = groupSel[gi] ?? [];
                  const done = sel.length >= maxQual;
                  const badgeColors = ['bg-yellow-400', 'bg-gray-400', 'bg-amber-600'];
                  const posLabels = ['1°', '2°', '3°'];
                  return (
                    <div key={group.name} className={`rounded-xl border-2 overflow-hidden transition-colors ${done ? 'border-green-500' : 'border-slate-600'}`}>
                      <div className={`flex items-center justify-between px-3 py-2 ${done ? 'bg-green-900/40' : 'bg-slate-700'}`}>
                        <span className="text-sm font-bold text-white">{groupName(group)}</span>
                        <div className="flex items-center gap-2">
                          {done && <span className="text-xs font-semibold text-green-600">✓ {sk('ready', 'Pronto')}</span>}
                          <button
                            onClick={(e) => { e.stopPropagation(); randomFillOneGroup(gi); }}
                            className="text-slate-400 hover:text-white transition-colors text-sm leading-none"
                            title={sk('draw_group', 'Sortear este grupo')}
                          >🎲</button>
                        </div>
                      </div>
                      <div className="p-2 space-y-1 bg-slate-800">
                        {group.teams.map(team => {
                          const pos = sel.findIndex(t => t.id === team.id);
                          const isSelected = pos >= 0;
                          const full = !isSelected && sel.length >= maxQual;
                          return (
                            <button key={team.id} onClick={() => toggleGroupTeam(gi, team)} disabled={full}
                              className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg transition-all text-left border-2 ${
                                isSelected ? 'bg-primary-900/50 border-primary-500' :
                                full ? 'opacity-30 cursor-not-allowed border-transparent' :
                                'hover:bg-slate-700 border-transparent'
                              }`}>
                              {isSelected
                                ? <div className={`w-6 h-6 rounded-full ${badgeColors[pos]} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>{posLabels[pos]}</div>
                                : <div className="w-6 h-6 rounded-full bg-slate-600 flex-shrink-0" />}
                              <Flag team={team} className="w-7 h-5" />
                              <span className="text-sm text-slate-200 font-medium truncate">{getTeamName(team)}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="text-center">
                <button onClick={goFromGroups} disabled={!allGroupsDone}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-600 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-sm transition-all">
                  {isCopa
                    ? sk('goto_thirds', 'Escolher Melhores Terceiros')
                    : isSudamericana
                      ? sk('goto_liberta', 'Escolher 3°s da Libertadores')
                      : sk('goto_knockout', 'Ir para o Mata-Mata')}
                  <ChevronRight className="w-5 h-5" />
                </button>
                {!allGroupsDone && <p className="text-xs text-slate-400 mt-2">{sk('complete_groups_hint', 'Complete todos os {{count}} grupos para continuar', { count: bracket.groups.length })}</p>}
              </div>
            </>
          )}
        </>
      )}

      {/* ══ MELHORES TERCEIROS ══════════════════════════════════════════════ */}
      {step === 'best_thirds' && (
        <>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-lg font-bold text-white">{sk('thirds_title', 'Melhores Terceiros Colocados')}</h3>
              <p className="text-sm text-slate-400 mt-0.5">
                {sk('thirds_hint', 'Selecione 8 dos 12 terceiros que avançam.')}{' '}
                <span className={`font-semibold ${bestThirds.length === 8 ? 'text-green-600' : 'text-primary-600'}`}>
                  {sk('thirds_selected', '{{count}}/8 selecionados', { count: bestThirds.length })}
                </span>
              </p>
            </div>
            <button
              onClick={randomFillBestThirds}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-semibold rounded-lg transition-colors flex-shrink-0"
            >
              🎲 Sortear
            </button>
          </div>
          <div className="w-full max-w-xs mx-auto bg-slate-700 rounded-full h-2 mb-6">
            <div className="bg-primary-600 h-2 rounded-full transition-all" style={{ width: `${(bestThirds.length / 8) * 100}%` }} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
            {bracket.groups.map((_g, gi) => {
              const team = groupSel[gi]?.[2];
              if (!team) return null;
              const isSelected = bestThirds.some(t => t.id === team.id);
              const canAdd = !isSelected && bestThirds.length < 8;
              return (
                <button key={team.id} onClick={() => toggleBestThird(team)} disabled={!isSelected && !canAdd}
                  className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    isSelected ? 'border-primary-500 bg-primary-900/50' :
                    canAdd ? 'border-slate-600 hover:border-primary-400 hover:bg-primary-900/30' :
                    'border-slate-700 opacity-30 cursor-not-allowed'
                  }`}>
                  <Flag team={team} className="w-9 h-6" />
                  <div className="text-left min-w-0">
                    <div className="text-xs font-bold text-slate-100">{team.code3}</div>
                    <div className="text-xs text-slate-400 truncate">{getTeamName(team)}</div>
                  </div>
                  {isSelected && (
                    <div className="ml-auto w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div className="text-center">
            <button onClick={goFromBestThirds} disabled={bestThirds.length !== 8}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-600 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-sm transition-all">
              {sk('goto_knockout', 'Ir para o Mata-Mata')} <ChevronRight className="w-5 h-5" />
            </button>
            {bestThirds.length !== 8 && <p className="text-xs text-slate-400 mt-2">{sk('thirds_exact_hint', 'Selecione exatamente 8 terceiros colocados')}</p>}
          </div>
        </>
      )}

      {/* ══ 3°s DA LIBERTADORES (Sudamericana) ════════════════════════════ */}
      {step === 'liberta_thirds' && bracket.libertadoresGroups && (
        <>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-lg font-bold text-white">{sk('liberta_title', '3° Colocados da Libertadores')}</h3>
              <p className="text-sm text-slate-400 mt-0.5">
                {sk('liberta_hint', 'Escolha o 3° colocado de cada grupo da Libertadores. Os 8 selecionados disputam os Playoffs da Sudamericana.')}{' '}
                <span className={`font-semibold ${completedLibertaGroups === bracket.libertadoresGroups.length ? 'text-green-600' : 'text-primary-600'}`}>
                  {sk('liberta_groups_count', '{{done}}/{{total}} grupos', { done: completedLibertaGroups, total: bracket.libertadoresGroups.length })}
                </span>
              </p>
            </div>
            <button
              onClick={randomFillLibertaThirds}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-semibold rounded-lg transition-colors flex-shrink-0"
            >
              🎲 Sortear
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {bracket.libertadoresGroups.map((group, gi) => {
              const sel = libertaThirds[gi] ?? [];
              const done = sel.length === 1;
              return (
                <div key={group.name} className={`rounded-xl border-2 overflow-hidden transition-colors ${done ? 'border-green-500' : 'border-slate-600'}`}>
                  <div className={`flex items-center justify-between px-3 py-2 ${done ? 'bg-green-900/40' : 'bg-slate-700'}`}>
                    <span className="text-sm font-bold text-white">{sk('liberta_prefix', 'Liberta')} · {groupName(group)}</span>
                    {done && <span className="text-xs font-semibold text-green-600">✓ {sk('liberta_chosen', '3° escolhido')}</span>}
                  </div>
                  <div className="p-2 space-y-1 bg-slate-800">
                    {group.teams.map(team => {
                      const isSelected = sel.some(t => t.id === team.id);
                      return (
                        <button key={team.id} onClick={() => toggleLibertaThird(gi, team)}
                          className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg transition-all text-left border-2 ${
                            isSelected ? 'bg-primary-900/50 border-primary-500' :
                            'hover:bg-slate-700 border-transparent'
                          }`}>
                          {isSelected
                            ? <div className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">3°</div>
                            : <div className="w-6 h-6 rounded-full bg-slate-600 flex-shrink-0" />}
                          <Flag team={team} className="w-7 h-5" />
                          <span className="text-sm text-slate-200 font-medium truncate">{getTeamName(team)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center">
            <button onClick={goFromLibertaThirds} disabled={!allLibertaThirdsDone}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-600 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-sm transition-all">
              {sk('goto_playoffs', 'Ir para os Playoffs')} <ChevronRight className="w-5 h-5" />
            </button>
            {!allLibertaThirdsDone && <p className="text-xs text-slate-400 mt-2">{sk('liberta_pick_hint', 'Escolha 1 time por grupo da Libertadores')}</p>}
          </div>
        </>
      )}

      {/* ══ MATA-MATA ═══════════════════════════════════════════════════════ */}
      {step === 'bracket' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">{sk('knockout_title', 'Mata-Mata')}</h3>
              <p className="text-sm text-slate-400 mt-0.5">
                {sk('knockout_hint', 'Toque no time para avançá-lo')} · <span className="font-semibold text-primary-400">{roundNames[curRound]}</span> {sk('knockout_in_progress', 'em andamento')}
              </p>
            </div>
            <button
              onClick={randomFillBracket}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-semibold rounded-lg transition-colors flex-shrink-0"
            >
              🎲 {sk('draw_round', 'Sortear rodada')}
            </button>
          </div>
          <BracketTreeView
            rounds={rounds}
            roundNames={roundNames}
            curRound={curRound}
            onPickWinner={pickWinner}
            isSudamericana={isSudamericana}
            toDefineLabel={sk('to_define', 'A definir')}
          />
          <div className="mt-4 text-center">
            {allMatchesDone ? (
              <p className="text-sm animate-pulse">
                {isLastRound
                  ? <span className="text-amber-400">🏆 {sk('revealing_champion', 'Revelando campeão...')}</span>
                  : <span className="text-primary-400">{sk('advancing_to', 'Avançando para {{round}}...', { round: roundNames[curRound + 1] })}</span>
                }
              </p>
            ) : (
              <p className="text-sm text-slate-400">{sk('matches_decided', '{{done}}/{{total}} jogos decididos em {{round}}', { done: decidedMatches, total: curMatches.length, round: roundNames[curRound] })}</p>
            )}
          </div>
        </>
      )}

      {/* ══ CAMPEÃO ═════════════════════════════════════════════════════════ */}
      {step === 'champion' && champion && (() => {
        const finalRound = rounds[rounds.length - 1];
        const finalMatch = finalRound?.[0];
        const runnerUp = finalMatch
          ? (finalMatch.team1?.id === champion.id ? finalMatch.team2 : finalMatch.team1)
          : null;

        return (
          <div className="text-center py-8 px-2">
            <div className="text-6xl mb-4 select-none" style={{ animation: 'bounce 2s infinite' }} aria-hidden="true">🏆</div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">{sk('champion_eyebrow', 'Campeão do Simulador')}</p>

            <div className="inline-flex items-center gap-4 bg-gradient-to-r from-yellow-900/40 to-amber-900/40 border border-yellow-700 rounded-2xl px-6 py-4 mb-5 shadow-sm">
              <Flag team={champion} className="w-20 h-14 rounded-lg shadow-md" />
              <div className="text-left">
                <div className="text-2xl sm:text-3xl font-black text-white">{champion.name}</div>
                <div className="text-sm text-yellow-400 font-bold">🥇 {sk('champion_label', 'Campeão')}</div>
              </div>
            </div>

            {runnerUp && (
              <div className="flex items-center justify-center gap-3 mb-6 opacity-70">
                <Flag team={runnerUp} className="w-10 h-7 rounded shadow-sm" />
                <div className="text-left">
                  <div className="text-sm font-bold text-slate-300">{runnerUp.name}</div>
                  <div className="text-xs text-slate-400">🥈 {sk('runnerup_label', 'Vice-campeão')}</div>
                </div>
              </div>
            )}

            <p className="text-slate-400 text-sm mb-6 max-w-sm mx-auto">
              {sk('champion_cta_text', 'Esta é a sua simulação! Compartilhe com os amigos e veja quem acerta o campeão de verdade.')}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={share} disabled={shareState === 'generating'}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-semibold rounded-xl transition-all shadow-sm">
                {shareState === 'generating'
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> {sk('share_generating', 'Gerando imagem...')}</>
                  : shareState === 'done'
                    ? <><Check className="w-4 h-4" /> {sk('share_done', 'Pronto!')}</>
                    : <><Share2 className="w-4 h-4" /> {sk('share', 'Compartilhar Simulação')}</>}
              </button>
              <button onClick={reset}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-slate-600 hover:border-primary-400 hover:text-primary-400 text-slate-400 font-semibold rounded-xl transition-all">
                <RotateCcw className="w-4 h-4" /> {sk('simulate_again', 'Simular Novamente')}
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
